import {LitElement, css, html, nothing} from "lit";
import {onceParentAvailable} from "../../utils/widgets.js";

/**
 * Map legend displays information about map sources.
 * @class Panoramax.components.menus.MapLegend
 * @element pnx-map-legend
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @example
 * ```html
 * <pnx-map-legend ._parent=${viewer} />
 * ```
 */
export default class MapLegend extends LitElement {
	/** @private */
	static styles = css`
		:host {
			font-size: 0.7em;
		}
		small {
			font-size: 1em;
		}
		.maplibregl-ctrl-attrib-inner {
			display: inline-block;
		}
		.maplibregl-ctrl-scale {
			font-size: 8px;
			white-space: nowrap;
			border-bottom: 1px solid var(--grey-dark);
			padding: 3px 0 0 3px;
			box-sizing: border-box;
			position: relative;
		}
		.maplibregl-ctrl-scale:before,
		.maplibregl-ctrl-scale:after {
			content: '';
			position: absolute;
			bottom: 0px;
			height: 3px;
		}
		.maplibregl-ctrl-scale:before {
			left: 0;
			border-left: 1px solid var(--grey-dark);
		}
		.maplibregl-ctrl-scale:after {
			right: 0;
			border-right: 1px solid var(--grey-dark);
		}
	`;

	/**
	 * Component properties.
	 * @memberof Panoramax.components.menus.MapLegend#
	 * @type {Object}
	 * @property {boolean} [light=false] Lighter version (for iframes)
	 */
	static properties = {
		light: {type: Boolean},
	};

	constructor() {
		super();
		this.light = false;
	}

	/** @private */
	connectedCallback() {
		super.connectedCallback();
		onceParentAvailable(this).then(() => this.requestUpdate());
		onceParentAvailable(this).then(() => this._parent.onceMapReady?.().then(() => {
			this._parent.map.on("styledata", () => this.requestUpdate());
			this._parent.map.on("data", () => this.requestUpdate());
			this.requestUpdate();
		}));
	}

	/** @private */
	render() {
		const mapAttrib = this._parent?.map?._attribution?._innerContainer;
		const mapLabelParts = this._parent?._t.map.map_data.split("{m}");
		const mapScale = this._parent?.map?._scale?._container;
		if(mapScale && this.light) { mapScale.remove(); }

		return this.light ?
			(mapAttrib && mapAttrib.innerHTML.length > 0 ? mapAttrib : nothing)
			: html`
				${mapAttrib && mapAttrib.innerHTML.length > 0
					? html`${mapLabelParts.shift()}${mapAttrib}${mapLabelParts.shift()}`
					: nothing}
				${mapScale}
			`;
	}
}

customElements.define("pnx-map-legend", MapLegend);
